import React, { useState, useEffect, useContext } from 'react'
import debounce from "lodash.debounce";
import { focusManager } from '@tanstack/react-query'
import { Box, CircularProgress } from '@mui/material'
import { useGetCurrentSession } from '../../services/api-query/auth'
import { useGetCustomer } from '../../services/api-query/customer'
import { sendSessionAnalytics } from '../../components/common/util/util'

import { 
  requestOtp,
  signInOtp,
  getAuthorizationCode,
  signInEmail, 
  signOutUser,
  changePassword,
  impersonationStaff 
} from '../../services/api-query/auth'

export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut,
}


const defaultState: any = {
  currentSession: {},
  authStatus: AuthStatus.Loading,
}

type Props = {
  children?: React.ReactNode
}


export const AuthContext = React.createContext(defaultState)

export const AuthIsSignedIn = ({ children }: Props) => {
  const { authStatus }: any = useContext(AuthContext)

  return <>{authStatus === AuthStatus.SignedIn ? children : null}</>
}

export const AuthIsNotSignedIn = ({ children }: Props) => {
  const { authStatus }: any = useContext(AuthContext)

  return <>{authStatus === AuthStatus.SignedOut ? children : null}</>
}

const AuthProvider = ({ children }: Props) => {
  const pathName = window.location.hash.substring(2) || '/';
  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading)
  const [userEmail, setEmail] = useState('')
  const [staff, setStaff] = useState('')
  const [impersonateError, setError] = useState('')
  const [impersonated, setImpersonated] = useState(false)
  const [loggedOut, setLoggedOut] = useState(false)
  const [otp, setOtp] = useState({} as any)
  const [authCode, setAuthCode] = useState('')
  const [passwordReset, setPasswordReset] = useState(false)
  const { 
    isPending, 
    errorSession, 
    refetchSession, 
    currentSession 
  } = useGetCurrentSession()
  const { refetchCustomer } = useGetCustomer(currentSession);
  
  useEffect(() => { 
    setStaff(impersonationStaff())
    focusManager.subscribe((isVisible) => {
      if(isVisible) sendSessionAnalytics(pathName, 'start')
      else sendSessionAnalytics(pathName, 'end')
    })
  },[])

  useEffect(() => { 
    try {
      if(!isPending) {
        if(errorSession) throw errorSession;
        if(!currentSession?.expiryMs || 
          !currentSession?.accessToken ||
          !currentSession?.idToken) {
          throw Error('Session not found!');
        }
        if(currentSession?.accessToken && 
           currentSession?.idToken) {
          setAuthStatus(AuthStatus.SignedIn)
        } else {
          if(!isPending) setAuthStatus(AuthStatus.SignedOut)
        }
      }
      if(loggedOut) {
        setAuthStatus(AuthStatus.SignedOut)
      }
    } catch {
      setAuthStatus(AuthStatus.SignedOut)
    }
  }, [
    authStatus, 
    currentSession, 
    errorSession, 
    isPending,
    loggedOut
  ])

  if (authStatus === AuthStatus.Loading) {
    return (
      <Box display="flex" 
          justifyContent={"center"}
          alignItems={"center"} 
          flexDirection={"row"} >
        <Box display="flex" 
            justifyContent={"center"} 
            alignItems={"center"} 
            flexDirection={"column"} 
            minHeight={"100vh"}>
            <CircularProgress />
        </Box>
      </Box>
    )
  }

  async function signOut(close?: Function) {
    sendSessionAnalytics('/signOut','end')
    await signOutUser()
    await refetchSession()
    setLoggedOut(true)
    setAuthStatus(AuthStatus.SignedOut)
    if(close) setTimeout(close,2000)
  }


  async function signInWithEmail(
      email: string, 
      password: string, 
      remember?: boolean) {
    try {
      setStaff('')
      setEmail(email)
      await signInEmail(email, password, remember)
      await refetchSession()
      await refetchCustomer()
      setLoggedOut(false)
      setAuthStatus(AuthStatus.SignedIn)
      sendSessionAnalytics('/signIn','start')
    } catch(err: any) {
      throw err;
    }
  }

  async function requestCode(
      email: string, 
      action='emailVerification'
  ) {
    setEmail(email)
    try {
      const otpResponse = await requestOtp(email, action)
      setOtp(otpResponse)
    } catch(err) {
      throw err;
    }
  }

  async function signInWithCode(code: string) {
    try {
      await signInOtp(otp.codeVerifier, otp.referenceId, code)
      await refetchSession()
      setLoggedOut(false)
      setAuthStatus(AuthStatus.SignedIn)
      setPasswordReset(true)
      setOtp({})
    } catch(err) {
      throw err;
    }
  }

  async function impersonate(
    code: string, 
    codeVerifier: string, 
    referenceId: string
  ) {
    try {
      await signInOtp(codeVerifier, referenceId, code)
      setImpersonated(true)
      await refetchSession()
      await refetchCustomer()
      setLoggedOut(false)
      setAuthStatus(AuthStatus.SignedIn)
      console.log('signInOtp', 'success')
      return null;
    } catch(err: any) {
      console.log('signInOtp', err?.message)
      setError(err?.message)
      return err?.message;
    }
  }

  async function getAuthCode(code: string) {
    try {
      const authCde = await getAuthorizationCode(
                              otp.codeVerifier, 
                              otp.referenceId || otp.id, code
                            )
      setAuthCode(authCde?.authorizationCode)
      setOtp({})
    } catch(err: any) {
      console.log('ERR', err?.message)
      throw err;
    }
  }

  const openPdf = (pdf: string) => {
    window.open(pdf,'_self') 
  }

  async function changeUserPassword(password: string, code?: string) {
    try {
      if(code && code?.length === 6) {
        await getAuthCode(code);
      }
      if(authCode) await changePassword(authCode, password)
    } catch(err) {
      throw err;
    }
  }

  const state: any = {
    authStatus,
    userEmail,
    otp, 
    setOtp,
    passwordReset,
    setPasswordReset,
    changeUserPassword,
    signInWithEmail,
    signInWithCode,
    openPdf: debounce(openPdf, 500, { trailing: true }),
    impersonate: debounce(impersonate, 1000, { trailing: true }),
    requestCode,
    getAuthCode,
    authCode,
    signOut,
    staff, 
    setStaff: (staffName: string) => {
      impersonationStaff(staffName)
      setStaff(staffName)
    },
    impersonated,
    impersonateError
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

export default AuthProvider
